/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";

export type LoadChatSecurityOption1 = {
  projectSlugHeaderGramProject: string;
  sessionHeaderGramSession: string;
};

export type LoadChatSecurityOption2 = {
  chatSessionsTokenHeaderGramChatSession: string;
};

export type LoadChatSecurityOption3 = {
  apikeyHeaderGramKey: string;
  projectSlugHeaderGramProject: string;
};

export type LoadChatSecurity = {
  option1?: LoadChatSecurityOption1 | undefined;
  option2?: LoadChatSecurityOption2 | undefined;
  option3?: LoadChatSecurityOption3 | undefined;
};

export type LoadChatRequest = {
  /**
   * The ID of the chat
   */
  id: string;
  /**
   * Generation to load. A generation is an immutable snapshot of the chat transcript: a new one is opened whenever the conversation is compacted or an earlier message is edited, while normal turns append to the current generation. Generations are numbered from 0 (oldest) up to `max_generation` (latest). Omit this attribute to receive the latest generation, or page through history by walking from `max_generation` down to 0.
   */
  generation?: number | undefined;
  /**
   * Maximum number of messages to return for this page.
   */
  limit?: number | undefined;
  /**
   * Keyset cursor: return the page of messages with `seq` strictly less than this value (older messages). The returned `messages` are always ordered oldest to newest by `seq`, like every other response. Use the `seq` of the oldest message you currently hold to load the previous page. Ignored when `risk_only` is set. Mutually exclusive with `after_seq`; if both are supplied, `after_seq` takes precedence.
   */
  beforeSeq?: number | undefined;
  /**
   * Keyset cursor: return the page of messages with `seq` strictly greater than this value (newer messages). The returned `messages` are always ordered oldest to newest by `seq`. Use the `seq` of the newest message you currently hold to load the next page. Ignored when `risk_only` is set. Mutually exclusive with `before_seq`; if both are supplied, `after_seq` takes precedence.
   */
  afterSeq?: number | undefined;
  /**
   * When true, return the oldest page of the generation (the start of the thread), ordered oldest to newest, with `has_more_before=false`. Page forward from there with `after_seq`. Ignored when `before_seq`, `after_seq`, or `risk_only` is set.
   */
  fromStart?: boolean | undefined;
  /**
   * When true, return only messages that have active risk findings, each padded with a fixed window of surrounding messages, grouped into contiguous segments (see `risk_segments`). The flagged messages themselves are marked with `is_risk` on each `ChatMessage` (surrounding context is `is_risk: false`). Cursors are ignored in this mode; expand a segment with a follow-up `before_seq`/`after_seq` request. Mutually exclusive with `query`.
   */
  riskOnly?: boolean | undefined;
  /**
   * When set (and `risk_only` is false), return only messages whose text matches this query (case-insensitive substring over message text, tool names/arguments, and structured content), each padded with a fixed window of surrounding messages, grouped into contiguous segments (see `match_segments`). Cursors are ignored on the initial request; expand a segment with a follow-up `before_seq`/`after_seq` request. Mutually exclusive with `risk_only`.
   */
  query?: string | undefined;
  /**
   * Session header
   */
  gramSession?: string | undefined;
  /**
   * project header
   */
  gramProject?: string | undefined;
  /**
   * Chat Sessions token header
   */
  gramChatSession?: string | undefined;
  /**
   * API Key header
   */
  gramKey?: string | undefined;
};

/** @internal */
export type LoadChatSecurityOption1$Outbound = {
  "project_slug_header_Gram-Project": string;
  "session_header_Gram-Session": string;
};

/** @internal */
export const LoadChatSecurityOption1$outboundSchema: z.ZodMiniType<
  LoadChatSecurityOption1$Outbound,
  LoadChatSecurityOption1
> = z.pipe(
  z.object({
    projectSlugHeaderGramProject: z.string(),
    sessionHeaderGramSession: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      projectSlugHeaderGramProject: "project_slug_header_Gram-Project",
      sessionHeaderGramSession: "session_header_Gram-Session",
    });
  }),
);

export function loadChatSecurityOption1ToJSON(
  loadChatSecurityOption1: LoadChatSecurityOption1,
): string {
  return JSON.stringify(
    LoadChatSecurityOption1$outboundSchema.parse(loadChatSecurityOption1),
  );
}

/** @internal */
export type LoadChatSecurityOption2$Outbound = {
  "chat_sessions_token_header_Gram-Chat-Session": string;
};

/** @internal */
export const LoadChatSecurityOption2$outboundSchema: z.ZodMiniType<
  LoadChatSecurityOption2$Outbound,
  LoadChatSecurityOption2
> = z.pipe(
  z.object({
    chatSessionsTokenHeaderGramChatSession: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      chatSessionsTokenHeaderGramChatSession:
        "chat_sessions_token_header_Gram-Chat-Session",
    });
  }),
);

export function loadChatSecurityOption2ToJSON(
  loadChatSecurityOption2: LoadChatSecurityOption2,
): string {
  return JSON.stringify(
    LoadChatSecurityOption2$outboundSchema.parse(loadChatSecurityOption2),
  );
}

/** @internal */
export type LoadChatSecurityOption3$Outbound = {
  "apikey_header_Gram-Key": string;
  "project_slug_header_Gram-Project": string;
};

/** @internal */
export const LoadChatSecurityOption3$outboundSchema: z.ZodMiniType<
  LoadChatSecurityOption3$Outbound,
  LoadChatSecurityOption3
> = z.pipe(
  z.object({
    apikeyHeaderGramKey: z.string(),
    projectSlugHeaderGramProject: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      apikeyHeaderGramKey: "apikey_header_Gram-Key",
      projectSlugHeaderGramProject: "project_slug_header_Gram-Project",
    });
  }),
);

export function loadChatSecurityOption3ToJSON(
  loadChatSecurityOption3: LoadChatSecurityOption3,
): string {
  return JSON.stringify(
    LoadChatSecurityOption3$outboundSchema.parse(loadChatSecurityOption3),
  );
}

/** @internal */
export type LoadChatSecurity$Outbound = {
  Option1?: LoadChatSecurityOption1$Outbound | undefined;
  Option2?: LoadChatSecurityOption2$Outbound | undefined;
  Option3?: LoadChatSecurityOption3$Outbound | undefined;
};

/** @internal */
export const LoadChatSecurity$outboundSchema: z.ZodMiniType<
  LoadChatSecurity$Outbound,
  LoadChatSecurity
> = z.pipe(
  z.object({
    option1: z.optional(z.lazy(() => LoadChatSecurityOption1$outboundSchema)),
    option2: z.optional(z.lazy(() => LoadChatSecurityOption2$outboundSchema)),
    option3: z.optional(z.lazy(() => LoadChatSecurityOption3$outboundSchema)),
  }),
  z.transform((v) => {
    return remap$(v, {
      option1: "Option1",
      option2: "Option2",
      option3: "Option3",
    });
  }),
);

export function loadChatSecurityToJSON(
  loadChatSecurity: LoadChatSecurity,
): string {
  return JSON.stringify(
    LoadChatSecurity$outboundSchema.parse(loadChatSecurity),
  );
}

/** @internal */
export type LoadChatRequest$Outbound = {
  id: string;
  generation?: number | undefined;
  limit: number;
  before_seq?: number | undefined;
  after_seq?: number | undefined;
  from_start: boolean;
  risk_only: boolean;
  query?: string | undefined;
  "Gram-Session"?: string | undefined;
  "Gram-Project"?: string | undefined;
  "Gram-Chat-Session"?: string | undefined;
  "Gram-Key"?: string | undefined;
};

/** @internal */
export const LoadChatRequest$outboundSchema: z.ZodMiniType<
  LoadChatRequest$Outbound,
  LoadChatRequest
> = z.pipe(
  z.object({
    id: z.string(),
    generation: z.optional(z.int()),
    limit: z._default(z.int(), 50),
    beforeSeq: z.optional(z.int()),
    afterSeq: z.optional(z.int()),
    fromStart: z._default(z.boolean(), false),
    riskOnly: z._default(z.boolean(), false),
    query: z.optional(z.string()),
    gramSession: z.optional(z.string()),
    gramProject: z.optional(z.string()),
    gramChatSession: z.optional(z.string()),
    gramKey: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      beforeSeq: "before_seq",
      afterSeq: "after_seq",
      fromStart: "from_start",
      riskOnly: "risk_only",
      gramSession: "Gram-Session",
      gramProject: "Gram-Project",
      gramChatSession: "Gram-Chat-Session",
      gramKey: "Gram-Key",
    });
  }),
);

export function loadChatRequestToJSON(
  loadChatRequest: LoadChatRequest,
): string {
  return JSON.stringify(LoadChatRequest$outboundSchema.parse(loadChatRequest));
}
